import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { WebLinksAddon } from 'xterm-addon-web-links';
import { WebglAddon } from 'xterm-addon-webgl';
import 'xterm/css/xterm.css';
import { X, Terminal as TerminalIcon, Loader2, AlertCircle } from 'lucide-react';

interface K8sPodShellModalProps {
    agentId: string;
    clientId: string;
    namespace: string;
    podName: string;
    containerName: string;
    onClose: () => void;
}

export const K8sPodShellModal: React.FC<K8sPodShellModalProps> = ({
    agentId,
    clientId,
    namespace,
    podName,
    containerName,
    onClose,
}) => {
    const [status, setStatus] = useState<'connecting' | 'connected' | 'error'>('connecting');
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const terminalRef = useRef<HTMLDivElement>(null);
    const xtermRef = useRef<Terminal | null>(null);
    const fitAddonRef = useRef<FitAddon | null>(null);
    const webglAddonRef = useRef<WebglAddon | null>(null);
    const wsRef = useRef<WebSocket | null>(null);

    // Initialize Terminal
    useEffect(() => {
        if (!terminalRef.current || xtermRef.current) return;

        const terminal = new Terminal({
            cursorBlink: true,
            fontSize: 14,
            fontFamily: 'Menlo, Monaco, "Courier New", monospace',
            theme: {
                background: '#000000',
                foreground: '#ffffff',
                cursor: '#f97316',
                cursorAccent: '#000000',
                selectionBackground: 'rgba(249, 115, 22, 0.3)',
                black: '#000000',
                red: '#ff4757',
                green: '#2ed573',
                yellow: '#ffa502',
                blue: '#00d4ff',
                magenta: '#ff6b9d',
                cyan: '#1e90ff',
                white: '#ffffff',
            },
        });

        const fitAddon = new FitAddon();
        terminal.loadAddon(fitAddon);
        terminal.loadAddon(new WebLinksAddon());

        terminal.open(terminalRef.current);

        try {
            const webglAddon = new WebglAddon();
            webglAddon.onContextLoss(() => {
                webglAddon.dispose();
            });
            terminal.loadAddon(webglAddon);
            webglAddonRef.current = webglAddon;
        } catch (e) {
            console.warn('WebGL addon could not be loaded, falling back to DOM renderer', e);
        }

        if (terminalRef.current.clientWidth > 0 && terminalRef.current.clientHeight > 0) {
            try {
                fitAddon.fit();
            } catch (e) { }
        }

        xtermRef.current = terminal;
        fitAddonRef.current = fitAddon;

        terminal.writeln('\x1b[36m╔════════════════════════════════════════╗\x1b[0m');
        terminal.writeln('\x1b[36m║            K8s Pod Shell               ║\x1b[0m');
        terminal.writeln('\x1b[36m╚════════════════════════════════════════╝\x1b[0m');
        terminal.writeln('');
        terminal.writeln(`\x1b[33mConnecting to pod: ${podName} (container: ${containerName})...\x1b[0m`);
        terminal.writeln('');

        // Handle terminal input
        terminal.onData((data) => {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send(
                    JSON.stringify({
                        type: 'input',
                        data: data,
                    })
                );
            }
        });

        // Handle window resize
        const handleResize = () => {
            if (fitAddonRef.current && xtermRef.current && terminalRef.current) {
                if (terminalRef.current.clientWidth > 0 && terminalRef.current.clientHeight > 0) {
                    try {
                        fitAddonRef.current.fit();
                        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                            wsRef.current.send(
                                JSON.stringify({
                                    type: 'resize',
                                    cols: xtermRef.current.cols,
                                    rows: xtermRef.current.rows,
                                })
                            );
                        }
                    } catch (e) {
                        console.error('Resize error', e);
                    }
                }
            }
        };

        window.addEventListener('resize', handleResize);

        const resizeObserver = new ResizeObserver(() => {
            handleResize();
        });
        resizeObserver.observe(terminalRef.current);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (terminalRef.current) {
                resizeObserver.unobserve(terminalRef.current);
            }
            resizeObserver.disconnect();

            try {
                if (webglAddonRef.current) {
                    webglAddonRef.current.dispose();
                }
            } catch (e) {
                console.error('Error disposing WebGL addon', e);
            }

            try {
                terminal.dispose();
            } catch (e) {
                console.error('Error disposing terminal', e);
            }

            xtermRef.current = null;
            webglAddonRef.current = null;
            fitAddonRef.current = null;
        };
    }, [podName, containerName]);

    // Start Session API call & WS setup
    useEffect(() => {
        let isMounted = true;

        const startSession = async () => {
            try {
                const token = localStorage.getItem('access_token') || '';

                const clusterName = new URLSearchParams(window.location.search).get('cluster_name');
                const requestBody: any = {
                    agent_id: parseInt(agentId, 10),
                    ...(clientId && { client_id: parseInt(clientId, 10) }),
                    type: 'k8s_exec',
                    namespace: namespace,
                    pod_name: podName,
                    container: containerName,
                    command: '/bin/sh',
                };
                if (clusterName) {
                    requestBody.cluster_name = clusterName;
                }

                const startResponse = await axios.post(
                    `${window.location.origin}/v3/shell/start`,
                    requestBody,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );

                if (!startResponse.data.status) {
                    throw new Error(startResponse.data.message || 'Failed to start session');
                }

                const { ws_url } = startResponse.data.data;

                // Connect WebSocket
                const wsEndpoint = ws_url.includes('?') ? `${ws_url}&token=${token}` : `${ws_url}?token=${token}`;

                const ws = new WebSocket(wsEndpoint);
                wsRef.current = ws;

                ws.onopen = () => {
                    if (isMounted) {
                        setStatus('connected');
                    }
                    if (xtermRef.current) {
                        xtermRef.current.writeln('\x1b[32m✓ Connected to K8s exec session\x1b[0m\r\n');
                        xtermRef.current.writeln('\x1b[33mWaiting for agent to connect to pod...\x1b[0m\r\n');

                        // Send initial resize
                        ws.send(
                            JSON.stringify({
                                type: 'resize',
                                cols: xtermRef.current.cols,
                                rows: xtermRef.current.rows,
                            })
                        );

                        setTimeout(() => {
                            if (xtermRef.current) xtermRef.current.focus();
                        }, 100);
                    }
                };

                ws.onmessage = (event) => {
                    try {
                        const msg = JSON.parse(event.data);
                        switch (msg.type) {
                            case 'output':
                                if (xtermRef.current) {
                                    xtermRef.current.write(msg.data);
                                }
                                break;
                            case 'agent_ready':
                                if (xtermRef.current) {
                                    xtermRef.current.writeln('\x1b[32m✓ Agent connected - establishing pod exec...\x1b[0m\r\n');
                                }
                                break;
                            case 'waiting_for_agent':
                                if (xtermRef.current) {
                                    xtermRef.current.writeln('\x1b[33mWaiting for agent to connect...\x1b[0m\r\n');
                                }
                                break;
                            case 'connected':
                                if (xtermRef.current) {
                                    xtermRef.current.clear();
                                    xtermRef.current.writeln('\x1b[32m✓ Pod Exec Connected\x1b[0m\r\n');
                                    setTimeout(() => xtermRef.current?.focus(), 50);
                                }
                                break;
                            case 'error':
                                if (xtermRef.current) {
                                    xtermRef.current.writeln(`\r\n\x1b[31mError: ${msg.message}\x1b[0m\r\n`);
                                }
                                if (isMounted) setStatus('error');
                                break;
                            case 'disconnected':
                                if (xtermRef.current) {
                                    xtermRef.current.writeln('\r\n\x1b[33mConnection closed\x1b[0m\r\n');
                                }
                                if (isMounted) setStatus('error');
                                break;
                            case 'agent_disconnected':
                                if (xtermRef.current) {
                                    xtermRef.current.writeln(`\r\n\x1b[31mAgent disconnected: ${msg.message}\x1b[0m\r\n`);
                                }
                                if (isMounted) setStatus('error');
                                break;
                            default:
                                break;
                        }
                    } catch (e) {
                        // Raw output
                        if (xtermRef.current) {
                            xtermRef.current.write(event.data);
                        }
                    }
                };

                ws.onerror = (err) => {
                    console.error('WebSocket error:', err);
                    if (isMounted) {
                        setStatus('error');
                        setErrorMsg('WebSocket connection error');
                    }
                };

                ws.onclose = (event) => {
                    console.log(`WebSocket connection closed: ${event.code} - ${event.reason || 'No reason'}`);
                    if (isMounted) {
                        setStatus('error');
                        setErrorMsg('Connection closed');
                    }
                };
            } catch (err: any) {
                console.error(err);
                let errMsg = err.response?.data?.message || err.message || 'Failed to initialize session';

                if (err.response?.status === 409) {
                    errMsg = 'Session is already in use.';
                }

                if (isMounted) {
                    setStatus('error');
                    setErrorMsg(errMsg);
                }
            }
        };

        startSession();

        return () => {
            isMounted = false;
            if (wsRef.current) {
                wsRef.current.close();
            }
        };
    }, [agentId, clientId, namespace, podName, containerName]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
            <div className="bg-gray-950 border border-gray-700 rounded-xl shadow-2xl w-full max-w-5xl h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                {/* Modal Header */}
                <div className="flex justify-between items-center px-4 py-3 border-b border-gray-800 bg-gray-900 shrink-0">
                    <div className="flex items-center gap-3">
                        <TerminalIcon className="text-orange-500 w-5 h-5" />
                        <h3 className="text-gray-100 font-mono text-sm">
                            shell — <span className="font-bold text-white">{podName}</span> <span className="text-gray-500">({containerName})</span>
                        </h3>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 bg-gray-900 px-2 py-1 rounded-lg border border-gray-800">
                            <span className="text-[10px] uppercase font-bold text-gray-500 tracking-wider">Status:</span>
                            <div className="flex items-center gap-1.5">
                                <div
                                    className={`w-2 h-2 rounded-full ${status === 'connected'
                                        ? 'bg-green-500 animate-pulse'
                                        : status === 'error'
                                            ? 'bg-red-500'
                                            : 'bg-orange-500 animate-pulse'
                                        }`}
                                />
                                <span className="text-[10px] uppercase font-bold text-gray-300 font-mono">{status}</span>
                            </div>
                        </div>
                        {status === 'connected' && (
                            <button
                                onClick={() => {
                                    if (wsRef.current) wsRef.current.close();
                                }}
                                className="px-3 py-1 bg-red-500/20 hover:bg-red-500/40 text-red-400 text-[11px] font-bold rounded-lg border border-red-500/30 transition-colors uppercase tracking-wider"
                            >
                                Disconnect
                            </button>
                        )}
                        <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Modal Body */}
                <div className="flex-1 flex flex-col bg-black relative min-h-0">
                    {/* Loader state */}
                    {status === 'connecting' && (
                        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm">
                            <Loader2 className="w-10 h-10 animate-spin text-orange-500 mb-4" />
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest animate-pulse font-mono">
                                Starting Shell Session...
                            </span>
                        </div>
                    )}

                    {/* Error state */}
                    {status === 'error' && (
                        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/90 backdrop-blur-sm p-8 text-center">
                            <AlertCircle className="w-12 h-12 mb-4 text-red-500" />
                            <h2 className="text-lg font-bold mb-2 text-red-400">Connection Failed</h2>
                            <p className="text-sm text-gray-400 max-w-md mb-6 font-mono">
                                {errorMsg || 'Unable to establish WebSocket shell session'}
                            </p>
                        </div>
                    )}

                    {/* Interactive Terminal Screen */}
                    <div
                        className="flex-1 relative w-full h-full p-2 cursor-text"
                        onClick={() => xtermRef.current?.focus()}
                    >
                        <div ref={terminalRef} className="absolute inset-0 p-2" />
                    </div>
                </div>
            </div>
        </div>
    );
};
